import { useEffect, useRef, useState } from 'react';

// VCR Effect Class
class VCREffect {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  config: {
    fps: number;
    blur: number;
    opacity: number;
    miny: number;
    miny2: number;
    num: number;
  };
  vcrInterval: number | null = null;

  constructor(canvas: HTMLCanvasElement, options: Partial<typeof this.config> = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.config = {
      fps: 60,
      blur: 1,
      opacity: 1,
      miny: 220,
      miny2: 220,
      num: 70,
      ...options
    };

    this.init();
  }

  init() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.opacity = this.config.opacity.toString();

    this.generateVCRNoise();
    window.addEventListener('resize', () => this.onResize());
  }

  onResize() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  generateVCRNoise() {
    if (this.config.fps >= 60) {
      if (this.vcrInterval) cancelAnimationFrame(this.vcrInterval);
      const animate = () => {
        this.renderTrackingNoise();
        this.vcrInterval = requestAnimationFrame(animate);
      };
      animate();
    } else {
      if (this.vcrInterval) clearInterval(this.vcrInterval);
      this.vcrInterval = setInterval(() => {
        this.renderTrackingNoise();
      }, 1000 / this.config.fps) as unknown as number;
    }
  }

  renderTrackingNoise(radius = 2) {
    const { canvas, ctx, config } = this;
    let { miny, miny2, num } = config;

    canvas.style.filter = `blur(${config.blur}px)`;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#fff';

    ctx.beginPath();
    for (let i = 0; i <= num; i++) {
      const x = Math.random() * canvas.width;
      const y1 = this.getRandomInt(miny += 3, canvas.height);
      const y2 = this.getRandomInt(0, miny2 -= 3);
      ctx.fillRect(x, y1, radius, radius);
      ctx.fillRect(x, y2, radius, radius);
      ctx.fill();

      this.renderTail(ctx, x, y1, radius);
      this.renderTail(ctx, x, y2, radius);
    }
    ctx.closePath();
  }

  renderTail(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number) {
    const n = this.getRandomInt(1, 50);
    const dirs = [1, -1];
    const dir = dirs[Math.floor(Math.random() * dirs.length)];

    for (let i = 0; i < n; i++) {
      const r = this.getRandomInt(radius - 0.01, radius);
      const dx = this.getRandomInt(1, 4) * dir;
      radius -= 0.1;
      ctx.fillRect((x += dx), y, r, r);
      ctx.fill();
    }
  }

  getRandomInt(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  destroy() {
    if (this.vcrInterval) {
      if (this.config.fps >= 60) {
        cancelAnimationFrame(this.vcrInterval);
      } else {
        clearInterval(this.vcrInterval);
      }
    }
    window.removeEventListener('resize', () => this.onResize());
  }
}

const Index = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const snowEffectRef = useRef<HTMLDivElement>(null);
  const vcrEffectRef = useRef<VCREffect | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const volumeKnobRef = useRef<HTMLDivElement>(null);

  const videoIds = [
    'J5g7PBYlN0A',
    'c4CVKbVtTsc', 
    '143aXLat70E', 
    'J5SSsT1O9gE', 
    'dAiomIGB3qo', 
    'lk6iJNSv-vY', 
    '7PgJLyeb6sM', 
    'wz0A7m1euy0'
  ];
  
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isChangingChannel, setIsChangingChannel] = useState(false);
  const [volume, setVolume] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const switchToVideo = (index: number) => {
    if (isChangingChannel || index === currentVideoIndex) return;
    
    setIsChangingChannel(true);
    
    if (snowEffectRef.current && iframeRef.current) {
      snowEffectRef.current.style.opacity = '1';
      setTimeout(() => {
        setCurrentVideoIndex(index);
        if (iframeRef.current) {
          iframeRef.current.src = `https://www.youtube.com/embed/${videoIds[index]}?autoplay=1&controls=0&loop=1&mute=1`;
        }
        if (snowEffectRef.current) {
          snowEffectRef.current.style.opacity = '0';
        }
        setIsChangingChannel(false);
      }, 1500);
    }
  };

  const switchToNextVideo = () => {
    const nextIndex = (currentVideoIndex + 1) % videoIds.length;
    switchToVideo(nextIndex);
  };

  const switchToPreviousVideo = () => {
    const prevIndex = currentVideoIndex === 0 ? videoIds.length - 1 : currentVideoIndex - 1;
    switchToVideo(prevIndex);
  };

  const channelUp = () => {
    const nextIndex = currentVideoIndex === videoIds.length - 1 ? 0 : currentVideoIndex + 1;
    switchToVideo(nextIndex);
  };

  const channelDown = () => {
    const prevIndex = currentVideoIndex === 0 ? videoIds.length - 1 : currentVideoIndex - 1;
    switchToVideo(prevIndex);
  };

  const handleVolumeKnobMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    document.addEventListener('mousemove', handleVolumeKnobMouseMove);
    document.addEventListener('mouseup', handleVolumeKnobMouseUp);
  };

  const handleVolumeKnobMouseMove = (e: MouseEvent) => {
    if (!isDragging || !volumeKnobRef.current) return;

    const rect = volumeKnobRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
    let degrees = (angle * 180 / Math.PI + 90) % 360;
    if (degrees < 0) degrees += 360;
    
    // Map degrees to volume (0-100)
    // Knob rotates from 225° (min) to 315° (max) for better UX
    let normalizedAngle = degrees;
    if (degrees > 180) normalizedAngle = degrees - 360;
    
    const minAngle = -135; // 225°
    const maxAngle = -45;  // 315°
    
    let newVolume = ((normalizedAngle - minAngle) / (maxAngle - minAngle)) * 100;
    newVolume = Math.max(0, Math.min(100, newVolume));
    
    setVolume(Math.round(newVolume));
  };

  const handleVolumeKnobMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleVolumeKnobMouseMove);
    document.removeEventListener('mouseup', handleVolumeKnobMouseUp);
  };

  const getKnobRotation = () => {
    // Map volume (0-100) to rotation angle (-135° to -45°)
    const minAngle = -135;
    const maxAngle = -45;
    return minAngle + (volume / 100) * (maxAngle - minAngle);
  };

  useEffect(() => {
    if (canvasRef.current) {
      vcrEffectRef.current = new VCREffect(canvasRef.current, {
        opacity: 1,
        miny: 220,
        miny2: 220,
        num: 70,
        fps: 60,
        blur: 1
      });
    }

    // Set up automatic video switching interval
    intervalRef.current = setInterval(switchToNextVideo, 30000);

    return () => {
      if (vcrEffectRef.current) {
        vcrEffectRef.current.destroy();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('mousemove', handleVolumeKnobMouseMove);
      document.removeEventListener('mouseup', handleVolumeKnobMouseUp);
    };
  }, [currentVideoIndex]);

  return (
    <div className="min-h-screen w-full bg-black flex justify-center items-center overflow-hidden relative">
      {/* TV Screen Frame */}
      <div 
        className="absolute w-[90vw] h-[50vw] max-w-full max-h-full z-10 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://alexandrevacassin.fr/codepen/old-tv/base.webp)'
        }}
      />
      
      {/* TV Container */}
      <div className="absolute mb-[3vw] w-[60vw] mr-[10vw] h-[45vw] max-w-full max-h-[70vh] z-[1] flex justify-center items-center overflow-hidden">
        {/* Canvas for VCR Effects */}
        <canvas 
          ref={canvasRef}
          id="canvas"
          className="absolute left-0 z-[9] w-full h-full max-w-full max-h-full"
          style={{ mixBlendMode: 'screen' }}
        />
        
        {/* YouTube Video */}
        <iframe
          ref={iframeRef}
          id="ytplayer"
          type="text/html"
          width="720"
          height="540"
          src={`https://www.youtube.com/embed/${videoIds[currentVideoIndex]}?autoplay=1&controls=0&loop=1&mute=1`}
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="w-full h-full max-w-full max-h-full"
          style={{ filter: 'contrast(1.2) brightness(1.1)' }}
        />
        
        {/* Glitch Effect */}
        <div 
          className="pointer-events-none absolute top-0 left-0 w-full h-full z-[2] animate-pulse"
          style={{
            background: 'url("https://upload.wikimedia.org/wikipedia/commons/0/02/Television_static.gif")',
            mixBlendMode: 'multiply',
            opacity: 0.3,
            animation: 'glitchMove 0.2s infinite linear'
          }}
        />
        
        {/* Scan Lines */}
        <div 
          className="absolute top-0 left-0 w-full h-full pointer-events-none z-[2]"
          style={{
            background: `repeating-linear-gradient(
              to bottom,
              rgba(0, 0, 0, 0) 0px,
              rgba(0, 0, 0, 0.2) 1px,
              rgba(0, 0, 0, 0) 2px
            )`
          }}
        />
        
        {/* Snow Effect */}
        <div 
          ref={snowEffectRef}
          className="absolute top-0 left-0 w-full h-full bg-cover opacity-0 z-[3] pointer-events-none transition-opacity duration-500 ease-in-out"
          style={{
            background: 'url("https://upload.wikimedia.org/wikipedia/commons/0/02/Television_static.gif")',
            backgroundSize: 'cover'
          }}
        />
      </div>
      
      {/* TV Control Panel */}
      <div className="absolute right-[5vw] top-1/2 transform -translate-y-1/2 z-20 flex flex-col gap-6">
        {/* Channel Display */}
        <div className="bg-black border-2 border-gray-600 rounded-lg p-3 shadow-lg">
          <div className="text-green-400 font-mono text-lg text-center mb-2 font-bold">
            CH
          </div>
          <div className="text-green-400 font-mono text-2xl text-center bg-black border border-gray-700 rounded px-3 py-1">
            {String(currentVideoIndex + 1).padStart(2, '0')}
          </div>
        </div>

        {/* Volume Control */}
        <div className="bg-gray-800 border-2 border-gray-600 rounded-lg p-4 shadow-lg">
          <div className="text-gray-300 text-xs text-center mb-3 font-bold">VOLUME</div>
          
          {/* Volume Knob Container */}
          <div className="relative w-20 h-20 mx-auto mb-3">
            {/* Knob Base */}
            <div 
              className="absolute inset-0 rounded-full cursor-pointer select-none"
              style={{
                background: 'radial-gradient(circle at 30% 30%, #6b7280, #374151, #1f2937)',
                boxShadow: 'inset 0 0 10px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)'
              }}
              onMouseDown={handleVolumeKnobMouseDown}
            >
              {/* Volume Level Indicator Ring */}
              <div className="absolute inset-1 rounded-full border-2 border-gray-600">
                <svg className="absolute inset-0 w-full h-full -rotate-45" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="4"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="35"
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="4"
                    strokeDasharray={`${(volume / 100) * 220} 220`}
                    strokeLinecap="round"
                    className="transition-all duration-200"
                  />
                </svg>
              </div>
              
              {/* Knob Handle */}
              <div 
                ref={volumeKnobRef}
                className="absolute inset-2 rounded-full transition-transform duration-200 ease-out"
                style={{
                  background: 'radial-gradient(circle at 25% 25%, #9ca3af, #6b7280, #4b5563)',
                  boxShadow: 'inset 2px 2px 4px rgba(255,255,255,0.1), inset -2px -2px 4px rgba(0,0,0,0.3)',
                  transform: `rotate(${getKnobRotation()}deg)`
                }}
              >
                {/* Knob Indicator Line */}
                <div 
                  className="absolute w-1 h-6 bg-white rounded-full opacity-80"
                  style={{
                    top: '8%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.5)'
                  }}
                />
                
                {/* Knob Center Dot */}
                <div 
                  className="absolute w-2 h-2 bg-gray-400 rounded-full"
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              </div>
            </div>
            
            {/* Volume Markings */}
            <div className="absolute inset-0">
              {[0, 25, 50, 75, 100].map((mark, index) => {
                const angle = -135 + (mark / 100) * 90;
                const x = 50 + 45 * Math.cos((angle - 90) * Math.PI / 180);
                const y = 50 + 45 * Math.sin((angle - 90) * Math.PI / 180);
                return (
                  <div
                    key={mark}
                    className="absolute w-0.5 h-2 bg-gray-400"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                      transformOrigin: 'center bottom'
                    }}
                  />
                );
              })}
            </div>
          </div>
          
          {/* Volume Display */}
          <div className="text-center">
            <div className="text-green-400 font-mono text-sm bg-black border border-gray-700 rounded px-2 py-1 inline-block">
              {volume}
            </div>
          </div>
        </div>

        {/* Channel Controls */}
        <div className="bg-gray-800 border-2 border-gray-600 rounded-lg p-4 shadow-lg">
          <div className="flex flex-col gap-3">
            {/* Channel Up Button */}
            <button
              onClick={channelUp}
              disabled={isChangingChannel}
              className="bg-gray-700 hover:bg-gray-600 active:bg-gray-800 border-2 border-gray-500 rounded-md px-4 py-3 text-white font-bold text-sm transition-all duration-100 shadow-md hover:shadow-lg active:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(145deg, #4a5568, #2d3748)',
                boxShadow: 'inset 2px 2px 4px #616e7c, inset -2px -2px 4px #1a202c'
              }}
            >
              CH ▲
            </button>
            
            {/* Channel Down Button */}
            <button
              onClick={channelDown}
              disabled={isChangingChannel}
              className="bg-gray-700 hover:bg-gray-600 active:bg-gray-800 border-2 border-gray-500 rounded-md px-4 py-3 text-white font-bold text-sm transition-all duration-100 shadow-md hover:shadow-lg active:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(145deg, #4a5568, #2d3748)',
                boxShadow: 'inset 2px 2px 4px #616e7c, inset -2px -2px 4px #1a202c'
              }}
            >
              CH ▼
            </button>
          </div>
        </div>

        {/* Quick Channel Selection */}
        <div className="bg-gray-800 border-2 border-gray-600 rounded-lg p-3 shadow-lg">
          <div className="text-gray-300 text-xs text-center mb-2 font-bold">PRESETS</div>
          <div className="grid grid-cols-2 gap-1">
            {videoIds.map((_, index) => (
              <button
                key={index}
                onClick={() => switchToVideo(index)}
                disabled={isChangingChannel}
                className={`text-xs font-mono py-1 px-2 rounded border transition-all duration-100 ${
                  currentVideoIndex === index
                    ? 'bg-red-600 border-red-500 text-white shadow-inner'
                    : 'bg-gray-700 border-gray-500 text-gray-300 hover:bg-gray-600 active:bg-gray-800'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Keyboard Instructions */}
      <div className="absolute bottom-4 left-4 z-20 bg-black bg-opacity-70 text-white text-xs p-3 rounded border border-gray-600">
        <div className="font-bold mb-1">CONTROLS:</div>
        <div>↑/↓ Channel Up/Down</div>
        <div>1-8 Direct Channel</div>
        <div>Volume: Drag knob</div>
      </div>
      
      {/* CSS Animation Styles */}
      <style jsx>{`
        @keyframes glitchMove {
          0% { transform: translateX(0); }
          33% { transform: translateX(-5px); }
          66% { transform: translateX(5px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default Index;